import "../styles/app.css";
import { api, ApiError } from "./api";
import { iniciarRouter, navegar, type Ruta } from "./router";
import type { Sesion } from "../lib/types";
import { renderLogin } from "./pages/login";
import { renderPedido } from "./pages/pedido";
import { renderPedidos } from "./pages/pedidos";
import { renderProduccion } from "./pages/produccion";
import { renderCuentas } from "./pages/cuentas";
import { renderClientes } from "./pages/clientes";
import { renderProductos } from "./pages/productos";
import { renderCategorias } from "./pages/categorias";
import { renderEmpresas } from "./pages/empresas";
import { renderListaPrecios } from "./pages/listaprecios";
import { renderSubas } from "./pages/subas";
import { renderInformes } from "./pages/informes";
import { renderClave } from "./pages/clave";
import { renderUsuarios } from "./pages/usuarios";
import { renderAuditoria } from "./pages/auditoria";
import { renderAyuda } from "./pages/ayuda";
import { logoApp, iconoMenu, iconoCerrar, iconoRuta, iconoFlechaDer, iconoAyuda } from "./iconos";

const nav = document.getElementById("nav") as HTMLElement;
const app = document.getElementById("app") as HTMLElement;

const RUTAS: Ruta[] = [
  { hash: "#/pedido", titulo: "Pedido", render: renderPedido, permiso: "pedido" },
  { hash: "#/pedidos", titulo: "Pedidos", render: renderPedidos, permiso: "pedido" },
  { hash: "#/produccion", titulo: "Produccion", render: renderProduccion, permiso: "produccion" },
  { hash: "#/cuentas", titulo: "Cuentas", render: renderCuentas, permiso: "cuentas" },
  { hash: "#/listas", titulo: "Lista de precios", render: renderListaPrecios, permiso: "listas" },
  { hash: "#/subas", titulo: "Subas", render: renderSubas, permiso: "subas" },
  { hash: "#/informes", titulo: "Informes", render: renderInformes, permiso: "informes" },
  { hash: "#/clientes", titulo: "Clientes", render: renderClientes, permiso: "clientes" },
  { hash: "#/productos", titulo: "Productos", render: renderProductos, permiso: "productos" },
  { hash: "#/categorias", titulo: "Categorias", render: renderCategorias, permiso: "categorias" },
  { hash: "#/empresas", titulo: "Empresas", render: renderEmpresas, permiso: "__admin" },
  { hash: "#/usuarios", titulo: "Usuarios", render: renderUsuarios, permiso: "__admin" },
  { hash: "#/auditoria", titulo: "Auditoria", render: renderAuditoria, permiso: "__admin" },
  { hash: "#/clave", titulo: "Cambiar clave", render: renderClave, oculta: true },
  { hash: "#/ayuda", titulo: "Centro de ayuda", render: renderAyuda, oculta: true },
];

// Rutas que el usuario logueado puede usar (admin ve todo).
function rutasPermitidas(s: Sesion): Ruta[] {
  if (s.es_admin) return RUTAS;
  return RUTAS.filter(
    (r) => !r.permiso || (r.permiso !== "__admin" && s.permisos.includes(r.permiso)),
  );
}

function marcarActivo(): void {
  const actual = location.hash || "#/pedido";
  document.querySelectorAll<HTMLAnchorElement>("a[data-hash]").forEach((a) => {
    a.classList.toggle("activo", a.dataset.hash === actual);
  });
}

function renderNav(usuario: string, rutas: Ruta[]): void {
  const visibles = rutas.filter((r) => !r.oculta);
  const linksDesktop = visibles
    .map(
      (r) =>
        `<a href="${r.hash}" data-hash="${r.hash}" class="nav-desktop border-b-2 border-transparent px-2 py-1 font-medium text-masa-600 transition hover:text-masa-900">${r.titulo}</a>`,
    )
    .join("");
  const linksMovil = visibles
    .map(
      (r) =>
        `<a href="${r.hash}" data-hash="${r.hash}" class="nav-movil flex items-center gap-3 rounded-xl px-4 py-4 text-lg font-medium text-masa-800 hover:bg-crema-100 active:bg-crema-200">
          <span class="text-masa-500">${iconoRuta(r.hash)}</span>
          <span class="flex-1">${r.titulo}</span>
          <span class="text-masa-300">${iconoFlechaDer}</span>
        </a>`,
    )
    .join("");

  nav.innerHTML = `
    <div class="mx-auto max-w-5xl px-4">
      <div class="flex items-center gap-2 py-2.5">
        <span class="flex flex-1 items-center gap-2 text-lg font-bold text-masa-900 sm:flex-none">
          ${logoApp()}PediPan
        </span>
        <nav class="hidden flex-1 flex-wrap items-center gap-3 text-sm sm:flex">${linksDesktop}</nav>
        <a href="#/ayuda" data-hash="#/ayuda" title="Centro de ayuda" class="rounded-lg p-1.5 text-masa-500 hover:bg-crema-100 hover:text-masa-800">${iconoAyuda}</a>
        <a href="#/clave" title="Cambiar clave" class="hidden rounded-lg px-2 py-1 text-sm text-masa-500 hover:bg-crema-100 hover:text-masa-800 sm:inline">${usuario}</a>
        <button id="salir" class="hidden rounded-lg px-3 py-1.5 text-sm text-masa-600 hover:bg-crema-100 sm:inline">Salir</button>
        <button id="hamb" class="rounded-lg p-2 text-masa-600 hover:bg-crema-100 sm:hidden" aria-label="Abrir menu">${iconoMenu}</button>
      </div>
    </div>`;

  // El overlay va en el body (no dentro del header, que tiene backdrop-blur y
  // crearia un bloque contenedor que rompe el position:fixed).
  document.getElementById("menuMovil")?.remove();
  const menuMovil = document.createElement("div");
  menuMovil.id = "menuMovil";
  menuMovil.className = "fixed inset-0 z-50 hidden sm:hidden";
  menuMovil.innerHTML = `
      <div id="backdrop" class="absolute inset-0 bg-black/40"></div>
      <div class="absolute inset-x-0 top-0 max-h-full overflow-y-auto rounded-b-3xl bg-crema-50 shadow-2xl">
        <div class="flex items-center justify-between border-b border-crema-200 px-4 py-3">
          <span class="flex items-center gap-2 text-lg font-bold text-masa-900">
            ${logoApp()}PediPan
          </span>
          <button id="cerrarMenu" class="chip-accion" aria-label="Cerrar">${iconoCerrar}</button>
        </div>
        <nav class="flex flex-col gap-1 p-2">${linksMovil}
          <a href="#/ayuda" data-hash="#/ayuda" class="nav-movil flex items-center gap-3 rounded-xl px-4 py-4 text-lg font-medium text-masa-800 hover:bg-crema-100 active:bg-crema-200">
            <span class="text-masa-500">${iconoAyuda}</span>
            <span class="flex-1">Centro de ayuda</span>
            <span class="text-masa-300">${iconoFlechaDer}</span>
          </a>
        </nav>
        <div class="flex items-center justify-between border-t border-crema-200 px-4 py-3">
          <a href="#/clave" data-hash="#/clave" class="text-sm text-masa-500 underline-offset-2 hover:underline">Sesion: <b>${usuario}</b> · Cambiar clave</a>
          <button id="salirMovil" class="btn-sec">Salir</button>
        </div>
      </div>`;
  document.body.appendChild(menuMovil);
  const abrir = () => menuMovil.classList.remove("hidden");
  const cerrar = () => menuMovil.classList.add("hidden");
  nav.querySelector("#hamb")?.addEventListener("click", abrir);
  menuMovil.querySelector("#cerrarMenu")?.addEventListener("click", cerrar);
  menuMovil.querySelector("#backdrop")?.addEventListener("click", cerrar);
  menuMovil.querySelectorAll("a[data-hash]").forEach((a) => a.addEventListener("click", cerrar));

  const salir = async () => { await api.post("/api/logout"); location.reload(); };
  nav.querySelector("#salir")?.addEventListener("click", salir);
  menuMovil.querySelector("#salirMovil")?.addEventListener("click", salir);

  marcarActivo();
  window.addEventListener("hashchange", marcarActivo);
}

async function iniciar(): Promise<void> {
  try {
    const s = await api.get<Sesion>("/api/sesion");
    const permitidas = rutasPermitidas(s);
    renderNav(s.usuario, permitidas);
    const primera = permitidas.find((r) => !r.oculta)?.hash ?? "#/clave";
    if (!location.hash || !permitidas.some((r) => r.hash === location.hash)) {
      location.hash = primera;
    }
    iniciarRouter(permitidas, app);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      nav.innerHTML = "";
      renderLogin(app, () => {
        void iniciar();
      });
    } else {
      app.innerHTML = `<div class="tarjeta text-red-700">Error al iniciar: ${
        e instanceof Error ? e.message : "desconocido"
      }</div>`;
    }
  }
}

// Exponer navegar por si alguna pagina lo necesita.
(window as unknown as { navegar: typeof navegar }).navegar = navegar;

void iniciar();
